/**
 *  @file       protocol/connection/validity-stamp-cache.js
 *  @author     Ryan Rossiter, ryan@kingsds.network
 *  @date       January 2020
 *
 * The validity stamp cache is used to ensure that no request from any connection
 * should have the same validity stamp as any other request (within the ttl period).
 * 
 * It periodically sweeps the cache for stamps that have passed their expiry period.
 *
 * @todo  Change this from a singleton to a per-target implemenation, so that
 *        we free up memory as soon as the target closes.
 */
'use strict';

const { assert } = require('dcp/common/dcp-assert');
const debugging = require('dcp/debugging').scope('dcp');
const { setInterval } = require('dcp/common/dcp-timers');

class ValidityStampCache {
  constructor() {
    this.stamps = { /* stamp: expiry time */ };
    this.initialized = false;
  }

  /**
   * Initialize the purge timer.
   */
  init()
  {
    var purgeInterval = dcpConfig.dcp.validityStampCachePurgeInterval;

    if (this.initialized)
      return;
    this.initialized = true;

    if (purgeInterval === 0)
    {
      debugging('validity') && console.debug('Disabling dcp message validity cache expiry');
      return;
    }

    if (!purgeInterval) 
      purgeInterval = 60; /* seconds */
      
    this.purgeIntervalTimerHnd = setInterval(() => this.purge(), purgeInterval);
    this.purgeIntervalTimerHnd.unref();
  }

  /**
   * Remove stamps from the cache that have expired.
   */
  purge()
  {
    const now = Date.now();
    for (let stamp in this.stamps) {
      if (this.stamps[stamp] <= now)
        delete this.stamps[stamp];
    }
  }

  /**
   * Clear identity cache completely, without regard for expired stamps.
   * This function should not be used lightly, if used in production services it will
   * allow replay attacks
   */
  clear()
  {
    this.stamps = {};
  }

  /**
   * Add a stamp to the cache
   * @param {string} stamp 
   * @param {number} expiry 
   *
   * @returns true on success, false on insert collision
   */
  insert(stamp, expiry) {
    if (!this.initialized)
      this.init();

    if (this.stamps.hasOwnProperty(stamp) && this.stamps[stamp] > Date.now())
      return false;
    
    assert(expiry > 0);
    
    // store ms for comparison with Date.now
    this.stamps[stamp] = expiry * 1000;
    return true;
  }
}

exports.validityStampCache = new ValidityStampCache();
